import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useT } from "../i18n";
import { errorKey } from "../lib/errors";
import { Icon, type IconName } from "./Icon";

export type ToastTone = "ok" | "info" | "warn" | "crit";
export type ToastInput = { message: ReactNode; tone?: ToastTone };
type ToastItem = ToastInput & { id: number };

/** How long a toast stays (ms). Errors stay until closed: they usually ask for an action. */
export const TOAST_MS = 5000;

const ICON: Record<ToastTone, IconName> = { ok: "check", info: "info", warn: "alert", crit: "alert" };

const ToastContext = createContext<(t: ToastInput) => void>(() => undefined);

/**
 * Short confirmations after an action ("Kaydedildi", "Davet gönderildi"). They never carry the only copy
 * of important information: errors that need a decision stay inline next to the form.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const t = useT();
  const [items, setItems] = useState<ToastItem[]>([]);
  const next = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    setItems((xs) => xs.filter((x) => x.id !== id));
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
  }, []);

  const push = useCallback(
    (input: ToastInput) => {
      const id = next.current++;
      setItems((xs) => [...xs.slice(-2), { ...input, id }]);
      if (input.tone !== "crit") timers.current.set(id, setTimeout(() => dismiss(id), TOAST_MS));
    },
    [dismiss],
  );

  useEffect(() => {
    const all = timers.current;
    return () => all.forEach((timer) => clearTimeout(timer));
  }, []);

  const value = useMemo(() => push, [push]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-region" aria-live="polite">
        {items.map((x) => {
          const tone = x.tone ?? "info";
          return (
            <div key={x.id} className={`toast tone-${tone}`} role={tone === "crit" ? "alert" : undefined}>
              <Icon name={ICON[tone]} size={18} />
              <div className="toast-text">{x.message}</div>
              <button type="button" className="icon-button" onClick={() => dismiss(x.id)} title={t("common.close")}>
                <Icon name="close" size={14} label={t("common.close")} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

/** `const toast = useToast(); toast({ message: t("…"), tone: "ok" })` */
export function useToast(): (t: ToastInput) => void {
  return useContext(ToastContext);
}

export type Feedback = {
  /** Short success confirmation ("Kaydedildi."). */
  ok(message: string): void;
  /** Readable error toast: an optional lead ("Kaydedilemedi.") plus the translated reason, never raw error text. */
  fail(err: unknown, lead?: string): void;
};

/**
 * Feedback after an action (ADR 0029). Success always gets a toast. A failure gets an error toast when the
 * action has no open form or dialog to show it in; forms and dialogs keep their inline error instead (it sits
 * next to the fields and stays until fixed), so the same error is never announced twice.
 */
export function useFeedback(): Feedback {
  const toast = useToast();
  const t = useT();
  return useMemo(
    () => ({
      ok: (message: string) => toast({ message, tone: "ok" }),
      fail: (err: unknown, lead?: string) => toast({ message: lead ? `${lead} ${t(errorKey(err))}` : t(errorKey(err)), tone: "crit" }),
    }),
    [toast, t],
  );
}
